<script setup lang="ts">
import type { IEventVaultSettingsUpdate, ISelectItem } from "@/types"
import { useMutation, useQueryClient } from "@tanstack/vue-query"
import { computed, reactive, ref, watchEffect } from "vue"
import { GET_EVENT_VAULT_QUERY_KEY, updateEventVaultSettings, useGetEventVault } from "@/api"
import { FreemiumBadge } from "@/components"
import { AppLink, Button, Chip, Form, FormSection, Icon, Select, Switch } from "@/components/ui"
import { useTranslation } from "@/composables"
import { EventWebsiteUrlMode, ExperienceType, isEventWebsiteUrlModeVisible } from "@/models"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { SocialsBehavior, TaggingBehavior } from "@/types"

defineOptions({
  inheritAttrs: false,
})

const { saveOnChange = true } = defineProps<{
  saveOnChange?: boolean
}>()

const emit = defineEmits<{
  success: []
  error: []
}>()

const eventStore = useEventVaultStore()

const settingBeingUpdated = ref<string | undefined>()
const { t } = useTranslation()
const queryClient = useQueryClient()

const { isLoading: isLoadingEventVault, data: eventVault } = useGetEventVault(eventStore.eventVaultId)

const { mutate, isPending: isUpdatingSetting } = useMutation({
  mutationFn: (eventVaultSettings: IEventVaultSettingsUpdate) =>
    updateEventVaultSettings(eventVaultSettings, eventStore.eventVaultId),
  onSuccess: async () => {
    await queryClient.invalidateQueries({ queryKey: [GET_EVENT_VAULT_QUERY_KEY] })

    setTimeout(() => {
      settingBeingUpdated.value = undefined
      emit("success")
    }, 300)
  },
  onError: async () => {
    settingBeingUpdated.value = undefined
    emit("error")
  },
})

const updateSettings = reactive<IEventVaultSettingsUpdate>({
  shareGalleryWithGuests: false,
  allowGuestDownload: false,
  allowGuestUpload: false,
  allowStreamingVids: false,
  hideMainGallery: false,
  enableGuestbook: false,
  allowGuestbookVideo: false,
  shareGuestbookWithGuests: false,
  hideGuestEmail: false,
  allowGuestZipDownload: false,
  enableReactions: false,
  enableComments: false,
  enableTags: false,
  experienceType: undefined,
  enableEventWebsite: false,
})

watchEffect(() => {
  if (eventVault?.value?.eventVaultSettings) {
    const settings = eventVault.value?.eventVaultSettings

    const { socialsBehavior, taggingBehavior } = settings

    updateSettings.shareGalleryWithGuests = settings.sharePixWithGuests
    updateSettings.allowGuestDownload = settings.allowGuestDownload
    updateSettings.allowGuestUpload = !settings.disableGuestUpload
    updateSettings.allowStreamingVids = settings.streamingAllowed
    updateSettings.hideMainGallery = settings.hideMainGallery
    updateSettings.enableGuestbook = !settings.hideGuestbook
    updateSettings.allowGuestbookVideo = settings.allowVideoGuestbook
    updateSettings.shareGuestbookWithGuests = settings.shareGuestbookWithGuests
    updateSettings.hideGuestEmail = settings.hideGuestEmail
    updateSettings.allowGuestZipDownload = settings.allowGuestZipDownload
    updateSettings.enableInvitation = settings.invitationEnabled
    updateSettings.moderatePhotosAndVideos = settings.moderatePhotosAndVideos
    updateSettings.moderateMessagesAndComments = settings.moderateMessagesAndComments
    updateSettings.experienceType = settings.experienceType
    updateSettings.enableEventWebsite
      = isEventWebsiteUrlModeVisible(settings.eventWebsiteUrlMode)
        ? settings.eventWebsiteUrlMode === EventWebsiteUrlMode.PathBased
        : undefined

    updateSettings.enableReactions
      = socialsBehavior === SocialsBehavior.Disabled
        ? undefined
        : socialsBehavior === SocialsBehavior.ReactionsOnly || socialsBehavior === SocialsBehavior.ReactionsAndComments

    updateSettings.enableComments
      = socialsBehavior === SocialsBehavior.Disabled
        ? undefined
        : socialsBehavior === SocialsBehavior.CommentsOnly || socialsBehavior === SocialsBehavior.ReactionsAndComments

    updateSettings.enableTags
      = taggingBehavior === TaggingBehavior.Disabled ? undefined : taggingBehavior === TaggingBehavior.On
  }
})

function submitUpdatedSettings(setting: string) {
  if (setting !== "all" && !saveOnChange)
    return
  if (setting)
    settingBeingUpdated.value = setting
  mutate(updateSettings)
}

const eventExperienceDescriptions: Record<ExperienceType, string> = {
  [ExperienceType.MultiPage]: t("event_settings_page.settings.multi_page_description"),
  [ExperienceType.SinglePage]: t("event_settings_page.settings.single_page_description"),
}

const eventExperienceItems: ISelectItem<ExperienceType>[] = [
  { value: ExperienceType.MultiPage, label: t("event_settings_page.settings.multi_page_label"), icon: "AppWindowMultiple" },
  { value: ExperienceType.SinglePage, label: t("event_settings_page.settings.single_page_label"), icon: "AppWindow" },
]

const hideModerateMediaSettings = computed(() => {
  return eventVault?.value?.eventVaultSettings?.moderatePhotosAndVideos === undefined || eventVault?.value?.eventVaultSettings?.moderatePhotosAndVideos === null
})

const isFreemiumEvent = computed(() => eventVault?.value?.freemiumMode)

const eventWebsiteSettingVisible = computed(() => isEventWebsiteUrlModeVisible(eventVault?.value?.eventVaultSettings?.eventWebsiteUrlMode))
</script>

<template>
  <div v-if="isLoadingEventVault" class="flex flex-col gap-6">
    <div v-for="n in 5" :key="n" class="flex flex-col gap-2 mb-8">
      <div class="gpx-skeleton w-3/12 h-10" />
      <hr>
      <div class="gpx-skeleton w-4/12 h-10" />
      <div class="flex md:gap-14 gap-2 md:flex-row flex-col">
        <div class="gpx-skeleton w-2/12 h-10" />
        <div class="gpx-skeleton h-20 grow" />
      </div>
    </div>
  </div>

  <Form v-show="!isLoadingEventVault" class="flex flex-col gap-6">
    <FormSection :title="t('event_settings_page.gallery_settings_title')" icon="Images">
      <!-- Allow guests to view the gallery -->
      <div>
        <h5 class="font-semibold">
          {{ t("event_settings_page.settings.allow_view_gallery_label") }}
        </h5>
        <div class="flex flex-col gap-2 md:flex-row items-start md:gap-14 mt-3">
          <Switch
            v-model="updateSettings.shareGalleryWithGuests"
            variant="icon"
            name="shareGalleryWithGuests"
            :is-loading="settingBeingUpdated === 'shareGalleryWithGuests'"
            :readonly="isUpdatingSetting"
            @change="() => submitUpdatedSettings('shareGalleryWithGuests')"
          />
          <p class="text-[#757575] text-sm md:text-base">
            {{ t("event_settings_page.settings.allow_view_gallery_description") }}
          </p>
        </div>
      </div>
      <hr>

      <!-- Allow guest to download photos -->
      <div>
        <h5 class="font-semibold">
          {{ t("event_settings_page.settings.allow_guest_download_label") }}
        </h5>
        <div class="flex flex-col gap-2 md:flex-row items-start md:gap-14 mt-3">
          <Switch
            v-model="updateSettings.allowGuestDownload"
            variant="icon"
            name="allowGuestDownload"
            :is-loading="settingBeingUpdated === 'allowGuestDownload'"
            :readonly="isUpdatingSetting"
            @change="() => submitUpdatedSettings('allowGuestDownload')"
          />
          <div class="text-[#757575] text-sm md:text-base">
            <p>{{ t("event_settings_page.settings.allow_guest_download_description1") }}</p>
            <br>
            <p>
              <i18n-t keypath="event_settings_page.settings.allow_guest_download_description2" scope="global">
                <template #termsOfService>
                  <a href="https://guestpix.com/host-terms-and-conditions" class="underline">{{
                    t("event_settings_page.settings.terms_of_service")
                  }}</a>
                </template>
                <template #termsAndConditions>
                  <a href="https://guestpix.com/guest-terms-and-conditions/" class="underline">{{
                    t("event_settings_page.settings.terms_and_conditions")
                  }}</a>
                </template>
              </i18n-t>
            </p>
          </div>
        </div>
      </div>
      <hr>

      <!-- Allow guests to upload to the gallery -->
      <div>
        <h5 class="font-semibold">
          {{ t("event_settings_page.settings.allow_guest_upload_label") }}
        </h5>
        <div class="flex flex-col gap-2 md:flex-row items-start md:gap-14 mt-3">
          <Switch
            v-model="updateSettings.allowGuestUpload"
            variant="icon"
            name="allowGuestUpload"
            :is-loading="settingBeingUpdated === 'allowGuestUpload'"
            :readonly="isUpdatingSetting"
            @change="() => submitUpdatedSettings('allowGuestUpload')"
          />
          <div class="text-[#757575] text-sm md:text-base">
            <p>{{ t("event_settings_page.settings.allow_guest_upload_description1") }}</p>
            <br>
            <p class="text-red-700">
              {{ t("event_settings_page.settings.allow_guest_upload_description2") }}
            </p>
          </div>
        </div>
      </div>

      <!-- Allow streaming video in the guest portal -->
      <div v-if="eventVault?.eventVaultSettings?.videoEnabled">
        <hr>
        <h5 class="font-semibold mt-5">
          {{ t("event_settings_page.settings.allow_video_steaming_label") }}
        </h5>
        <div class="flex flex-col gap-2 md:flex-row items-start md:gap-14 mt-3">
          <Switch
            v-model="updateSettings.allowStreamingVids"
            variant="icon"
            name="allowStreamingVids"
            :is-loading="settingBeingUpdated === 'allowStreamingVids'"
            :readonly="isUpdatingSetting"
            @change="() => submitUpdatedSettings('allowStreamingVids')"
          />
          <p class="text-[#757575] text-sm md:text-base">
            {{ t("event_settings_page.settings.allow_video_steaming_description") }}
          </p>
        </div>
      </div>
      <hr>

      <!-- Hide Main Gallery -->
      <div v-if="eventVault?.eventVaultSettings?.albumsEnabled">
        <h5 class="font-semibold">
          {{ t("event_settings_page.settings.hide_main_gallery_label") }}
        </h5>
        <div class="flex flex-col gap-2 md:flex-row items-start md:gap-14 mt-3">
          <Switch
            v-model="updateSettings.hideMainGallery"
            variant="icon"
            name="hideMainGallery"
            :disabled="Boolean(!eventVault?.totalAlbums)"
            :is-loading="settingBeingUpdated === 'hideMainGallery'"
            :readonly="isUpdatingSetting"
            @change="() => submitUpdatedSettings('hideMainGallery')"
          />
          <div class="text-[#757575] text-sm md:text-base">
            <p>{{ t("event_settings_page.settings.hide_main_gallery_description1") }}</p>
            <br>
            <p>{{ t("event_settings_page.settings.hide_main_gallery_description2") }}</p>
          </div>
        </div>
      </div>
    </FormSection>

    <FormSection :title="t('event_settings_page.guestbook_settings_title')" icon="BookOpen">
      <!-- Enable the Guestbook -->
      <div>
        <h5 class="font-semibold">
          {{ t("event_settings_page.settings.enable_guestbook_label") }}
        </h5>
        <div class="flex flex-col gap-2 md:flex-row items-start md:gap-14 mt-3">
          <Switch
            v-model="updateSettings.enableGuestbook"
            variant="icon"
            name="enableGuestbook"
            :is-loading="settingBeingUpdated === 'enableGuestbook'"
            :readonly="isUpdatingSetting"
            @change="() => submitUpdatedSettings('enableGuestbook')"
          />
          <p class="text-[#757575] text-sm md:text-base">
            {{ t("event_settings_page.settings.enable_guestbook_description") }}
          </p>
        </div>
      </div>
      <hr>

      <!-- Share Guestbook content with guests -->
      <div>
        <h5 class="font-semibold">
          {{ t("event_settings_page.settings.share_guestbook_label") }}
        </h5>
        <div class="flex flex-col gap-2 md:flex-row items-start md:gap-14 mt-3">
          <Switch
            v-model="updateSettings.shareGuestbookWithGuests"
            variant="icon"
            name="shareGuestbookWithGuests"
            :is-loading="settingBeingUpdated === 'shareGuestbookWithGuests'"
            :readonly="isUpdatingSetting"
            @change="() => submitUpdatedSettings('shareGuestbookWithGuests')"
          />
          <p class="text-[#757575] text-sm md:text-base">
            {{ t("event_settings_page.settings.share_guestbook_description") }}
          </p>
        </div>
      </div>

      <!-- Allow videos in the Guest book -->
      <div v-if="isFreemiumEvent || eventVault?.eventVaultSettings?.videoEnabled">
        <hr>
        <div class="flex items-center gap-2 mt-5">
          <FreemiumBadge v-if="isFreemiumEvent" variant="icon" />
          <h5 class="font-semibold">
            {{ t("event_settings_page.settings.allow_video_guestbook_label") }}
          </h5>
        </div>
        <div class="flex flex-col gap-2 md:flex-row items-start md:gap-14 mt-3">
          <Switch
            v-model="updateSettings.allowGuestbookVideo"
            :disabled="isFreemiumEvent"
            variant="icon"
            name="allowGuestbookVideo"
            :is-loading="settingBeingUpdated === 'allowGuestbookVideo'"
            :readonly="isUpdatingSetting"
            @change="() => submitUpdatedSettings('allowGuestbookVideo')"
          />
          <p class="text-[#757575] text-sm md:text-base">
            {{ t("event_settings_page.settings.allow_video_guestbook_description") }}
          </p>
        </div>
      </div>
    </FormSection>

    <FormSection :title="t('event_settings_page.general_settings_title')" icon="SettingsCog">
      <!-- Hide Guest Email -->
      <div v-if="!isFreemiumEvent">
        <div class="flex items-center gap-2 mt-5">
          <h5 class="font-semibold">
            {{ t("event_settings_page.settings.require_guest_email_label") }}
          </h5>
        </div>
        <div class="flex flex-col gap-2 md:flex-row items-start md:gap-14 mt-3">
          <Switch
            v-model="updateSettings.hideGuestEmail"
            variant="icon"
            name="hideGuestEmail"
            :is-loading="settingBeingUpdated === 'hideGuestEmail'"
            :readonly="isUpdatingSetting"
            :disabled="isFreemiumEvent"
            @change="() => submitUpdatedSettings('hideGuestEmail')"
          />
          <div class="text-[#757575] text-sm md:text-base">
            <p>{{ t("event_settings_page.settings.require_guest_email_description1") }}</p>
            <br>
            <p>{{ t("event_settings_page.settings.require_guest_email_description2") }}</p>
          </div>
        </div>
        <hr class="mt-6">
      </div>

      <!-- Allow Guests ZIP Download -->
      <div>
        <div class="flex items-center gap-2 mt-5">
          <FreemiumBadge v-if="isFreemiumEvent" variant="icon" />
          <h5 class="font-semibold">
            {{ t("event_settings_page.settings.allow_zip_download_label") }}
          </h5>
        </div>
        <div class="flex flex-col gap-2 md:flex-row items-start md:gap-14 mt-3">
          <Switch
            v-model="updateSettings.allowGuestZipDownload"
            variant="icon"
            name="allowGuestZipDownload"
            :is-loading="settingBeingUpdated === 'allowGuestZipDownload'"
            :readonly="isUpdatingSetting"
            :disabled="isFreemiumEvent"
            @change="() => submitUpdatedSettings('allowGuestZipDownload')"
          />
          <div class="text-[#757575] text-sm md:text-base">
            <p>{{ t("event_settings_page.settings.allow_zip_download_description1") }}</p>
            <br>
            <p>
              <i18n-t keypath="event_settings_page.settings.allow_zip_download_description2" scope="global">
                <template #termsOfService>
                  <a href="https://guestpix.com/host-terms-and-conditions" class="underline">{{
                    t("event_settings_page.settings.terms_of_service")
                  }}</a>
                </template>
                <template #termsAndConditions>
                  <a href="https://guestpix.com/guest-terms-and-conditions/" class="underline">{{
                    t("event_settings_page.settings.terms_and_conditions")
                  }}</a>
                </template>
              </i18n-t>
            </p>
          </div>
        </div>
      </div>

      <div v-if="eventVault?.eventVaultSettings?.socialsBehavior !== SocialsBehavior.Disabled">
        <!-- Enable Reactions Interactions -->
        <hr>
        <h5 class="font-semibold mt-5">
          {{ t("event_settings_page.settings.enable_reactions_label") }}
        </h5>
        <div class="flex flex-col gap-2 md:flex-row items-start md:gap-14 my-3">
          <Switch
            v-model="updateSettings.enableReactions"
            variant="icon"
            name="enableReactions"
            :is-loading="settingBeingUpdated === 'enableReactions'"
            :readonly="isUpdatingSetting"
            @change="() => submitUpdatedSettings('enableReactions')"
          />
          <p class="text-[#757575] text-sm md:text-base">
            {{ t("event_settings_page.settings.enable_reactions_description") }}
          </p>
        </div>

        <!-- Enable Comments Interactions -->
        <hr>
        <h5 class="font-semibold mt-5">
          {{ t("event_settings_page.settings.enabled_commenting_label") }}
        </h5>
        <div class="flex flex-col gap-2 md:flex-row items-start md:gap-14 mt-3">
          <Switch
            v-model="updateSettings.enableComments"
            variant="icon"
            name="enableComments"
            :is-loading="settingBeingUpdated === 'enableComments'"
            :readonly="isUpdatingSetting"
            @change="() => submitUpdatedSettings('enableComments')"
          />
          <p class="text-[#757575] text-sm md:text-base">
            {{ t("event_settings_page.settings.enable_commenting_description") }}
          </p>
        </div>
      </div>

      <!-- Enable Tags -->
      <div v-if="eventVault?.eventVaultSettings?.taggingBehavior !== TaggingBehavior.Disabled">
        <hr>
        <h5 class="font-semibold mt-5">
          {{ t("event_settings_page.settings.enable_tagging_label") }}
        </h5>
        <div class="flex flex-col gap-2 md:flex-row items-start md:gap-14 mt-3">
          <Switch
            v-model="updateSettings.enableTags"
            variant="icon"
            name="enableTags"
            :is-loading="settingBeingUpdated === 'enableTags'"
            :readonly="isUpdatingSetting"
            @change="() => submitUpdatedSettings('enableTags')"
          />
          <p class="text-[#757575] text-sm md:text-base">
            {{ t("event_settings_page.settings.enable_tagging_description") }}
          </p>
        </div>
      </div>

      <!-- Enable website settings -->
      <div v-if="eventWebsiteSettingVisible">
        <hr>
        <h5 class="font-semibold mt-5">
          {{ t("event_settings_page.settings.enable_event_website_label") }}
        </h5>
        <div class="flex flex-col gap-2 md:flex-row items-start md:gap-14 mt-3">
          <Switch
            v-model="updateSettings.enableEventWebsite"
            variant="icon"
            name="enableEventWebsite"
            :is-loading="settingBeingUpdated === 'enableEventWebsite'"
            :readonly="isUpdatingSetting"
            @change="() => submitUpdatedSettings('enableEventWebsite')"
          />
          <div class="text-[#757575] text-sm md:text-base">
            <p>{{ t("event_settings_page.settings.enable_event_website_description") }}</p>
            <AppLink
              v-if="updateSettings.enableEventWebsite"
              :to="`/event/${eventStore.eventVaultId}/website`"
              class="text-link hover:text-link-hover hover:underline focus:underline inline-block mt-1"
            >
              {{ t("event_settings_page.settings.enable_event_website_manage_link") }}
            </AppLink>
          </div>
        </div>
      </div>
    </FormSection>

    <FormSection v-if="!hideModerateMediaSettings" :title="t('event_settings_page.settings.content_moderation_title')" icon="Flag">
      <!-- Enable the Invitation -->
      <div>
        <h5 class="font-semibold">
          {{ t("event_settings_page.settings.moderate_photos_and_videos_label") }}
        </h5>
        <div class="flex flex-col gap-2 md:flex-row items-start md:gap-14 mt-3">
          <Switch
            v-model="updateSettings.moderatePhotosAndVideos"
            variant="icon"
            name="moderatePhotosAndVideos"
            :is-loading="settingBeingUpdated === 'moderatePhotosAndVideos'"
            :readonly="isUpdatingSetting"
            @change="() => submitUpdatedSettings('moderatePhotosAndVideos')"
          />
          <p class="text-[#757575] text-sm md:text-base">
            {{ t("event_settings_page.settings.moderate_photos_and_videos_description") }}
          </p>
        </div>
      </div>
    </FormSection>

    <FormSection :title="t('event_settings_page.settings.guest_experience')" icon="AppWindow">
      <div data-test="event-settings-event-experience">
        <div class="flex flex-col gap-4 mt-3">
          <Select
            v-model="updateSettings.experienceType"
            name="experienceType"
            :label="t('event_settings_page.settings.experience_type_label')"
            :items="eventExperienceItems"
            :disabled="isUpdatingSetting"
            data-test="event-settings-event-experience-select"
            @update:model-value="submitUpdatedSettings('experienceType')"
          >
            <template #item="{ item }">
              <div class="flex flex-col gap-1 py-0.5 text-start">
                <div class="flex flex-wrap items-center gap-2">
                  <Icon v-if="item.icon" :icon="item.icon" class="w-4 h-4" />
                  <span class="font-semibold text-gray-700">{{ item.label }}</span>
                  <Chip
                    v-if="item.value === ExperienceType.SinglePage"
                    variant="pink-solid"
                    class="uppercase"
                    size="sm"
                    :label="t('new')"
                  />
                </div>
                <span class="text-xs font-normal leading-snug text-gray-600">
                  {{ eventExperienceDescriptions[item.value as ExperienceType] }}
                </span>
              </div>
            </template>
          </Select>
        </div>
      </div>
    </FormSection>

    <template #actions>
      <div class="mt-5 flex gap-2 items-center justify-end">
        <Button
          size="lg"
          class="w-full md:w-auto"
          :disabled="isUpdatingSetting || settingBeingUpdated === 'all'"
          @click="() => submitUpdatedSettings('all')"
        >
          {{ t("submit") }}
        </Button>
        <Icon v-if="settingBeingUpdated === 'all'" icon="Loading" />
      </div>
    </template>
  </Form>
</template>
